import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllEngagementsForInsightCheck.graphql';
import { Query, QueryallEngagementsForInsightCheckArgs } from '../../__generated__/globalTypes';

export const useAllEngagementsForInsightCheckQuery = (
  options?: QueryHookOptions<Query, QueryallEngagementsForInsightCheckArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallEngagementsForInsightCheckArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllEngagementsForInsightCheckLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallEngagementsForInsightCheckArgs>
): QueryResult<Query, QueryallEngagementsForInsightCheckArgs> & {
  getAllEngagementsForInsightCheck: LazyQueryExecFunction<Query, QueryallEngagementsForInsightCheckArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllEngagementsForInsightCheck, result] = useLazyQuery<Query, QueryallEngagementsForInsightCheckArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAllEngagementsForInsightCheck, ...result };
};

export const useAllEngagementsForInsightCheckPromiseQuery = () => {
  const client = useApolloClient();

  const getAllEngagementsForInsightCheck = useCallback(
    (options?: Omit<QueryOptions<QueryallEngagementsForInsightCheckArgs, Query>, 'query'>) =>
      client.query<Query, QueryallEngagementsForInsightCheckArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllEngagementsForInsightCheck };
};
// Suspense Query Hooks
export const useAllEngagementsForInsightCheckSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallEngagementsForInsightCheckArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallEngagementsForInsightCheckArgs>(QUERY, {
    ...options
  });

  return result;
};
